import { useEffect, useState } from "react";
import type { AttendanceTrend } from "@dentacore/shared-types";

import { readAttendance } from "../api/home";
import styles from "./attendance-panel.module.css";

/**
 * How reliably this location's patients turn up — Anthony's QA, item 17.
 *
 * His words: *"first page i would love to see graphs or charts in the first
 * page displaying percentage of people not coming and who misses their
 * appointments"*. The sentence stops mid-thought — there was an "as well as"
 * with nothing after it — so this builds the half that was legible and leaves
 * room beside it for whatever the rest turns out to be.
 *
 * ── Why a drawn SVG and not a chart library ─────────────────────────────────
 *
 * The web app has three runtime dependencies. A chart library would be the
 * fourth and the largest, for one twelve-point line on one screen — and every
 * one of them ships its own colours, which is the thing `tokens:check` exists
 * to prevent. Twelve bars and a line are forty lines of SVG that inherit the
 * palette for free and are legible in both themes without configuring anything.
 *
 * ── What it refuses to draw ─────────────────────────────────────────────────
 *
 * **A rate for a week nobody was booked in.** The server sends those weeks as
 * zeroes rather than skipping them — so the bars show an empty fortnight as
 * empty — but the *line* has no value there and lifts rather than crossing at
 * zero. A miss rate of 0% and "no appointments to miss" are different facts,
 * and a line drawn through the second reads as the first.
 */
export function AttendancePanel({
  branchId,
  day,
  bare = false,
}: {
  branchId: string | null;
  /** The branch's own local date. Never the browser's clock. */
  day: string;
  /**
   * Inside Home's Attendance tab (CS4): no card of its own and no title, the
   * tab names it. The chart, the three figures and the list stay full size.
   */
  bare?: boolean;
}): React.JSX.Element | null {
  const [trend, setTrend] = useState<AttendanceTrend | null>(null);

  useEffect(() => {
    if (branchId === null || day === "") return;

    const controller = new AbortController();
    setTrend(null);

    readAttendance(day, controller.signal)
      .then((read) => setTrend(read))
      .catch(() => {
        // Silent, like every other panel on this screen: a figure that could
        // not be read must not blank a dashboard somebody is working from.
      });

    return () => controller.abort();
  }, [branchId, day]);

  if (branchId === null) return null;

  return (
    <section className={bare ? styles.bare : styles.panel} data-testid="attendance-panel">
      <div className={styles.head}>
        {!bare && <h2 className={styles.title}>Attendance</h2>}
        <span className={styles.period}>
          {trend === null ? "…" : `${String(trend.weeks.length)} weeks`}
        </span>
      </div>

      {trend === null ? (
        <p className={styles.empty}>Reading…</p>
      ) : trend.attended + trend.noShow + trend.cancelled === 0 ? (
        <p className={styles.empty} data-testid="attendance-empty">
          No appointments have finished at this location yet, so there is nothing to measure.
        </p>
      ) : (
        <>
          {/*
            The chart first (EB8): the tester asked for a chart rather than a
            percentage, and it was below three figures they did not scroll to.
          */}
          <TrendChart trend={trend} />

          <div className={styles.figures}>
            <Figure
              label="Missed"
              value={trend.noShowRate === null ? "—" : `${percent(trend.noShowRate)}%`}
              note={`${String(trend.noShow)} of ${String(trend.attended + trend.noShow)} finished`}
              tone={toneFor(trend.noShowRate)}
              testId="attendance-no-show-rate"
            />
            <Figure
              label="Attended"
              value={String(trend.attended)}
              note="appointments kept"
              tone="calm"
              testId="attendance-attended"
            />
            <Figure
              label="Cancelled"
              value={String(trend.cancelled)}
              /* Reported, never counted as a miss — cancelling is the patient
                 telling you, and K4 already draws that line for the risk
                 flags. Charting the two alike would train patients not to
                 ring. */
              note="told us in advance"
              tone="calm"
              testId="attendance-cancelled"
            />
          </div>

          {trend.worstAttenders.length > 0 && (
            <div className={styles.missers} data-testid="attendance-missers">
              <h3 className={styles.subTitle}>Missing most appointments</h3>
              {trend.worstAttenders.map((person) => (
                <div key={person.patientId} className={styles.misser}>
                  <span className={styles.misserName}>{person.patientName}</span>
                  <span className={styles.misserMeta}>{person.patientFileNumber}</span>
                  <span className={styles.misserCount}>
                    {person.noShowCount} missed · {percent(person.noShowRate)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Bars for the volume, a line for the rate. Both read without colour. */
function TrendChart({ trend }: { trend: AttendanceTrend }): React.JSX.Element {
  const weeks = trend.weeks;
  const width = 100;
  const height = 40;
  const step = weeks.length <= 1 ? width : width / weeks.length;

  const busiest = Math.max(1, ...weeks.map((week) => week.attended + week.noShow + week.cancelled));

  /**
   * The rate line, broken across weeks that have no rate.
   *
   * One `<polyline>` per unbroken run rather than one for the lot: a null week
   * inside a single polyline is either dropped — joining the weeks either side
   * as though the gap were not there — or drawn at zero, which claims a perfect
   * week in a fortnight nobody was booked.
   */
  const runs: { x: number; y: number }[][] = [];
  let run: { x: number; y: number }[] = [];

  weeks.forEach((week, index) => {
    if (week.noShowRate === null) {
      if (run.length > 0) runs.push(run);
      run = [];
      return;
    }
    run.push({ x: index * step + step / 2, y: height - week.noShowRate * height });
  });
  if (run.length > 0) runs.push(run);

  return (
    <figure className={styles.chartWrap}>
      <svg
        className={styles.chart}
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={
          trend.noShowRate === null
            ? "No finished appointments to measure"
            : `Missed-appointment rate by week. ${percent(trend.noShowRate)}% over the whole period`
        }
        data-testid="attendance-chart"
      >
        {weeks.map((week, index) => {
          const total = week.attended + week.noShow + week.cancelled;
          const barHeight = (total / busiest) * height;

          return (
            <rect
              key={week.weekStart}
              className={styles.bar}
              x={index * step + step * 0.15}
              y={height - barHeight}
              width={step * 0.7}
              height={barHeight}
            >
              <title>
                {`Week of ${week.weekStart}: ${String(week.attended)} attended, ${String(
                  week.noShow,
                )} missed, ${String(week.cancelled)} cancelled`}
              </title>
            </rect>
          );
        })}

        {/*
          **A run of one is a dot, not a line.** A `<polyline>` with a single
          point draws nothing at all, so a practice with one week of history —
          or one week between two closed fortnights — saw bars and no rate
          anywhere, which reads as the rate being unavailable rather than as it
          being a single week. Caught in the running app; the data was right and
          the picture was empty.
        */}
        {runs.map((points) =>
          points.length === 1 ? (
            <circle
              key={String(points[0]?.x ?? 0)}
              className={styles.point}
              cx={points[0]?.x ?? 0}
              cy={points[0]?.y ?? 0}
              r={1.4}
            />
          ) : (
            <polyline
              key={String(points[0]?.x ?? 0)}
              className={styles.line}
              fill="none"
              vectorEffect="non-scaling-stroke"
              points={points.map((point) => `${String(point.x)},${String(point.y)}`).join(" ")}
            />
          ),
        )}
      </svg>
      <figcaption className={styles.caption}>
        Bars are appointments a week; the line is the share missed. {trend.from} to {trend.to}.
      </figcaption>
    </figure>
  );
}

function Figure({
  label,
  value,
  note,
  tone,
  testId,
}: {
  label: string;
  value: string;
  note: string;
  tone: "calm" | "warm";
  testId: string;
}): React.JSX.Element {
  return (
    <div className={styles.figure} data-tone={tone} data-testid={testId}>
      <span className={styles.figureLabel}>{label}</span>
      <span className={styles.figureValue}>{value}</span>
      <span className={styles.figureNote}>{note}</span>
    </div>
  );
}

/**
 * One in ten missed is where a practice starts losing real money, and it is the
 * figure the trade press quotes — so it is the point the number stops being
 * neutral. Below it, warming the card would cry wolf on an ordinary month.
 */
const CONCERNING_MISS_RATE = 0.1;

function toneFor(rate: number | null): "calm" | "warm" {
  return rate !== null && rate >= CONCERNING_MISS_RATE ? "warm" : "calm";
}

function percent(rate: number): string {
  return String(Math.round(rate * 100));
}
